import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/common/Modal";
import {
  TDistributorSpecialPrice,
  TDistributorSpecialPriceForm,
  DistributorSpecialPriceTable,
  DistributorSpecialPriceForm,
  DistributorSpecialPriceHeader,
} from "../../features/distributorSpecialPrices";
import { useIsAuth } from "../../utils/useAuth";
import { FormikHelpers, useFormik } from "formik";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  useDistributorSpecialPriceStatusToggle,
  useDistributorSpecialPrice,
  useSaveDistributorSpecialPrice,
  distributorSpecialPriceSchema,
} from "../../features/distributorSpecialPrices";

const DistributorSpecialPricePage = () => {
  const { distributorId = "" } = useParams();
  const { userData } = useIsAuth();
  const [showModal, setShowModal] = useState<boolean>(false);

  const formDataInit: TDistributorSpecialPriceForm = useMemo(() => {
    return {
      productId: "",
      distributorId,
      price: 0,
      effectiveFrom: new Date(),
      effectiveTo: null,
      createdBy: userData?.userId || "",
      active: true,
    };
  }, [userData?.userId]);

  // Hooks
  const { pagination, handlePageChange } = usePagination();
  // Fetch product price history list
  const { distributorProductSpecialPrices, paginationData } =
    useDistributorSpecialPrice({
      filter: {
        distributorId,
      },
      pagination,
    });

  // Mutation to save product price history
  const { handleSave } = useSaveDistributorSpecialPrice();

  const { handleEnable, handleDisable } =
    useDistributorSpecialPriceStatusToggle();

  const formik = useFormik<TDistributorSpecialPriceForm>({
    initialValues: formDataInit,
    validationSchema: distributorSpecialPriceSchema,
    onSubmit: (
      values: TDistributorSpecialPriceForm,
      formikHelpers: FormikHelpers<TDistributorSpecialPriceForm>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false); // You can perform additional actions here if needed
      formikHelpers.resetForm();
      handleClose();
    },
  });

  const handleOpenNewPriceHistoryModal = () => {
    formik.resetForm();
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    formik.resetForm();
  };

  const handleEdit = (productPrice: TDistributorSpecialPrice) => {
    setShowModal(true);
    formik.setValues(productPrice);
  };

  return (
    <div>
      <DistributorSpecialPriceHeader
        onNewPriceHistory={handleOpenNewPriceHistoryModal}
      />
      <DistributorSpecialPriceTable
        data={distributorProductSpecialPrices} // TODO: add data
        isLoading={false} // TODO: add loading state
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: (row) => handleEdit(row),
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id),
            shouldShow: (row) => row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleClose} submit={formik.handleSubmit}>
        <DistributorSpecialPriceForm formik={formik} />
      </Modal>
    </div>
  );
};

export default DistributorSpecialPricePage;
